import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Lock, FileText, Download, ShieldCheck, Clock, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { docTypeLabel, DocTypeIcon } from "./BusinessDocuments";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";

function StatusPill({ status }) {
  const map = {
    pending:  { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock,        text: "Pendiente" },
    approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: ShieldCheck, text: "Acceso aprobado" },
    rejected: { cls: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle,      text: "Rechazado" },
    revoked:  { cls: "bg-slate-100 text-slate-700 border-slate-200", icon: AlertTriangle, text: "Revocado" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.text}
    </span>
  );
}

export default function InvestorDocuments({ businessId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [open, setOpen] = useState(false);
  const [justification, setJustification] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/businesses/${businessId}/documents/public`);
      setDocs(r.data.items || []);
      setStatus(r.data.access_status);
      setAuthRequired(!!r.data.auth_required);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [businessId]);

  // Not authenticated as investor/admin → show login CTA
  if (authRequired) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="docs-auth-required">
        <div className="w-12 h-12 mx-auto rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mb-3">
          <Lock className="w-5 h-5" />
        </div>
        <p className="font-display font-bold text-sm">Documentos privados del negocio</p>
        <p className="mt-2 text-xs text-slate-500 max-w-md mx-auto">
          Los documentos sensibles (balance, contratos, patentes) sólo están disponibles para inversionistas autorizados por el equipo DerechoLlave dentro de su sesión.
        </p>
        <Button asChild className="mt-4 btn-primary" data-testid="docs-login-cta">
          <Link to="/login">Iniciar sesión como inversionista</Link>
        </Button>
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center text-sm text-slate-500" data-testid="docs-empty">
        Este negocio aún no tiene documentos cargados.
      </div>
    );
  }

  const isInvestor = user?.role === "investor";
  const approved = status === "approved";

  const openRequest = () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "investor") { toast.error("Sólo inversionistas pueden solicitar acceso a documentos"); return; }
    setOpen(true);
  };

  const requestAccess = async () => {
    if (!user) { toast.error("Inicia sesión como inversionista"); navigate("/login"); return; }
    if (user.role !== "investor") { toast.error("Sólo inversionistas pueden solicitar acceso"); return; }
    setLoading(true);
    try {
      await api.post(`/businesses/${businessId}/documents/request-access`, {
        business_id: businessId,
        justification: justification || null,
      });
      toast.success("Solicitud enviada. El equipo DerechoLlave decidirá el acceso.");
      setOpen(false);
      setJustification("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally {
      setLoading(false);
    }
  };

  const download = (doc) => {
    const token = localStorage.getItem("lln_token");
    fetch(`${API_BASE}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Acceso denegado"); return r.blob(); })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = doc.name;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("No tienes acceso aprobado a este documento"));
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="business-documents-block">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-600" />
          <h4 className="font-display font-bold text-sm">Documentos privados ({docs.length})</h4>
        </div>
        {status && <StatusPill status={status} />}
      </div>

      <ul className="space-y-2">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-zinc-50" data-testid={`pub-doc-${d.id}`}>
            <div className="w-8 h-8 rounded bg-zinc-100 text-slate-600 flex items-center justify-center shrink-0">
              <DocTypeIcon type={d.doc_type} className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{d.name}</p>
              <p className="text-xs text-slate-500">{docTypeLabel(d.doc_type)}</p>
            </div>
            {approved ? (
              <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`inv-doc-download-${d.id}`}>
                <Download className="w-4 h-4 mr-1" /> Descargar
              </Button>
            ) : (
              <Lock className="w-4 h-4 text-slate-400" />
            )}
          </li>
        ))}
      </ul>

      {!approved && (
        <div className="mt-4 pt-4 border-t border-zinc-100">
          {status === "pending" ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
              Tu solicitud está pendiente de aprobación del equipo DerechoLlave.
            </p>
          ) : status === "rejected" ? (
            <div className="space-y-2">
              <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md p-3">El equipo DerechoLlave rechazó tu acceso a estos documentos.</p>
              <Button onClick={openRequest} variant="outline" size="sm" data-testid="redo-doc-request">Volver a solicitar</Button>
            </div>
          ) : (
            <Button
              onClick={openRequest}
              className="w-full btn-primary"
              data-testid="request-docs-access"
            >
              <Lock className="w-4 h-4 mr-2" /> Solicitar acceso a documentos
            </Button>
          )}
          {isInvestor && (
            <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
              Los documentos son confidenciales. El equipo DerechoLlave decide quién accede dentro de su sesión. Está prohibido redistribuirlos.
              <Link to="/terminos" target="_blank" className="underline ml-1">Ver términos</Link>.
            </p>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar acceso a documentos privados</DialogTitle>
            <DialogDescription>
              El equipo DerechoLlave revisará tu solicitud antes de habilitar la descarga.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              data-testid="docs-justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={4}
              placeholder="Ej. Soy inversionista evaluando este negocio. Necesito ver el balance y el contrato de arriendo para tomar la decisión."
            />
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900 flex items-start gap-2">
              <FileText className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Al pedir acceso, aceptas usar la información sólo para evaluar este negocio. La plataforma audita todas las descargas.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={requestAccess} disabled={loading} className="btn-investment" data-testid="confirm-docs-request">
              {loading ? "Enviando…" : "Enviar solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
